import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ChevronRight, Fingerprint, Download } from "lucide-react";

const STATUS_COLORS = {
  pending: "#8E9196",
  running: "#00F0FF",
  completed: "#34C759",
  failed: "#FF3B30",
};

export default function Investigations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/investigations");
      setItems(data);
    } catch (_e) {
      toast.error("Failed to load investigations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Fingerprint className="w-3.5 h-3.5" /> Investigation-first workflow
          </div>
          <h1 className="font-display font-black text-5xl mt-2">Investigations</h1>
          <p className="text-[#8E9196] mt-1">Multi-source deep-dive with attack origin, IOC history, and court-ready reports.</p>
        </div>
        <Link
          to="/investigations/new"
          data-testid="new-investigation-btn"
          className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors px-4 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest"
        >
          <Plus className="w-4 h-4" /> New investigation
        </Link>
      </header>

      <div className="border border-white/10 bg-[#121212] rounded-sm overflow-hidden" data-testid="investigations-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              <th className="text-left px-5 py-3">Case</th>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Severity</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">IOCs</th>
              <th className="text-left px-5 py-3">Verdict</th>
              <th className="text-left px-5 py-3">Investigator</th>
              <th className="text-right px-5 py-3">Opened</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {items.map((inv) => {
              const verdict = inv.narrative?.verdict || "-";
              return (
                <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" data-testid={`invest-row-${inv.id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/investigations/${inv.id}`} className="font-mono text-[#00F0FF]">{inv.case_number}</Link>
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-white">{inv.customer?.customer_name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
                      {inv.customer?.organization || ""} {inv.customer?.industry ? "· " + inv.customer.industry : ""}
                    </div>
                    {inv.tech_tags && inv.tech_tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`tech-tags-${inv.id}`}>
                        {inv.tech_tags.slice(0, 4).map((t) => (
                          <span key={t} className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#B57BFF]/15 text-[#B57BFF] border border-[#B57BFF]/40">
                            🎯 {t}
                          </span>
                        ))}
                        {inv.tech_tags.length > 4 && (
                          <span className="text-[9px] font-mono text-[#8E9196]">+{inv.tech_tags.length - 4}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-[#FF9F0A]">
                    {inv.customer?.severity}
                  </td>
                  <td className="px-5 py-3">
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm"
                      style={{ color: STATUS_COLORS[inv.status], borderColor: STATUS_COLORS[inv.status] + "55" }}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs">{inv.evidence?.length || 0}</td>
                  <td className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest">{verdict.replace("_", " ")}</td>
                  <td className="px-5 py-3 text-xs text-[#8E9196]">{inv.investigator || "-"}</td>
                  <td className="px-5 py-3 text-right font-mono text-[10px] text-[#52525B]">{new Date(inv.created_at).toLocaleString()}</td>
                  <td className="px-2"><Link to={`/investigations/${inv.id}`}><ChevronRight className="w-4 h-4 text-[#8E9196]" /></Link></td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="py-16 text-center">
                  <div className="text-[#52525B] mb-4">No investigations yet.</div>
                  <Link to="/investigations/new" className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
                    <Plus className="w-4 h-4" /> Start your first investigation
                  </Link>
                </td>
              </tr>
            )}
            {loading && (
              <tr><td colSpan={9} className="py-10 text-center text-sm text-[#52525B]">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
